import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CheckSquare, Square } from "lucide-react";
import { getAllMenuList } from "../../../redux/admin/action";
import {
  PERMISSION_LABELS,
  getApplicablePermissions,
  normalizePermissionLevels,
} from "../../../lib/permissions";
import "./PermissionsMatrix.css";

const ACTIONS = PERMISSION_LABELS;

// For a given menu id, return the subset of ACTIONS that's actually meaningful.
// Field-level menus like Product Costs only support view/edit/export; create
// and delete don't apply.
const getRowActions = (menuId) => {
  const applicable = new Set(getApplicablePermissions(menuId));
  return ACTIONS.filter((a) => applicable.has(a.key));
};

// The admin detail response pairs every permission row with its menu via two fields:
// `id` mirrors the menu id, and `permission_id` is the admin_permissions row PK.
// Title is used as a defensive fallback in case the join field is renamed server-side.
const buildRows = (menuList, existingPermissions = []) => {
  const titleToMenuId = new Map();
  (menuList || []).forEach((m) => {
    if (m?.title) titleToMenuId.set(String(m.title).trim().toLowerCase(), Number(m.id));
  });

  const byMenuId = new Map();
  existingPermissions.forEach((p) => {
    let menuId = Number(p.menu_id ?? p.id);
    if (!Number.isFinite(menuId) && p.title) {
      menuId = titleToMenuId.get(String(p.title).trim().toLowerCase());
    }
    if (!Number.isFinite(menuId)) return;
    byMenuId.set(menuId, {
      id: p.permission_id ?? p.id ?? null,
      permission_level: normalizePermissionLevels(p.permission_level),
    });
  });

  return (menuList || []).map((menu) => {
    const existing = byMenuId.get(Number(menu.id));
    return {
      id: existing?.id ?? null,
      menu_id: Number(menu.id),
      title: menu.title,
      permission_level: existing?.permission_level || [],
    };
  });
};

const allActionsChecked = (row) => {
  const rowActions = getRowActions(row.menu_id);
  if (rowActions.length === 0) return false;
  return rowActions.every((a) => row.permission_level.includes(a.key));
};

const togglePermission = (row, actionKey) => {
  const rowActions = getRowActions(row.menu_id);
  if (!rowActions.some((a) => a.key === actionKey)) return row;

  const has = row.permission_level.includes(actionKey);
  const next = has
    ? row.permission_level.filter((a) => a !== actionKey)
    : [...row.permission_level, actionKey];

  // Keep the canonical label order so payloads are stable across renders.
  const ordered = ACTIONS.filter((a) => next.includes(a.key)).map((a) => a.key);
  return { ...row, permission_level: ordered };
};

const toggleAll = (row) => {
  if (allActionsChecked(row)) {
    return { ...row, permission_level: [] };
  }
  return {
    ...row,
    permission_level: getRowActions(row.menu_id).map((a) => a.key),
  };
};

const toPayload = (rows) =>
  rows
    .filter((r) => r.id != null || r.permission_level.length > 0)
    .map((r) => ({
      ...(r.id != null ? { id: r.id } : {}),
      menu_id: r.menu_id,
      permission_level: r.permission_level,
    }));

// `forceAllChecked` renders every applicable cell as checked without mutating
// the underlying `value`. Used when a target is a super admin: the matrix is
// shown as fully granted (matching runtime behaviour) but the user's staged
// permissions are preserved for after demotion.
const PermissionsMatrix = ({
  value,
  onChange,
  disabled = false,
  forceAllChecked = false,
}) => {
  const dispatch = useDispatch();
  const menuList = useSelector((state) => state.reusableSlice.MenuListData);

  useEffect(() => {
    dispatch(getAllMenuList());
  }, [dispatch]);

  const rows = useMemo(
    () => buildRows(menuList, value),
    [menuList, value],
  );

  const isReadOnly = disabled || forceAllChecked;

  const updateRow = (nextRow) => {
    const next = rows.map((r) =>
      r.menu_id === nextRow.menu_id ? nextRow : r,
    );
    onChange?.(toPayload(next));
  };

  const setAllRows = (checked) => {
    const next = rows.map((r) => ({
      ...r,
      permission_level: checked
        ? getRowActions(r.menu_id).map((a) => a.key)
        : [],
    }));
    onChange?.(toPayload(next));
  };

  const totalCells = rows.reduce(
    (acc, r) => acc + getRowActions(r.menu_id).length,
    0,
  );
  const totalChecked = forceAllChecked
    ? totalCells
    : rows.reduce((acc, r) => acc + r.permission_level.length, 0);
  const allChecked = totalChecked === totalCells && totalCells > 0;

  return (
    <div className="permissions-matrix">
      <div className="permissions-matrix__header">
        <div>
          <h4 className="permissions-matrix__title">Permissions</h4>
          <p className="permissions-matrix__subtitle">
            Grant fine-grained access per module. {allChecked ? "All access granted." : `${totalChecked} of ${totalCells} permissions granted.`}
          </p>
        </div>
        {!isReadOnly && (
          <button
            type="button"
            className="permissions-matrix__bulk"
            onClick={() => setAllRows(!allChecked)}
          >
            {allChecked ? (
              <>
                <CheckSquare size={14} /> Clear all
              </>
            ) : (
              <>
                <Square size={14} /> Grant all
              </>
            )}
          </button>
        )}
      </div>

      <div className="permissions-matrix__table-wrap">
        <table className="permissions-matrix__table">
          <thead>
            <tr>
              <th scope="col" className="permissions-matrix__module-col">
                Module
              </th>
              {ACTIONS.map((a) => (
                <th key={a.key} scope="col">
                  {a.label}
                </th>
              ))}
              <th scope="col">All</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={ACTIONS.length + 2}
                  className="permissions-matrix__empty"
                >
                  Loading modules…
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const applicableKeys = new Set(
                  getRowActions(row.menu_id).map((a) => a.key),
                );
                const rowAllChecked = forceAllChecked
                  ? applicableKeys.size > 0
                  : allActionsChecked(row);
                return (
                  <tr key={row.menu_id}>
                    <th scope="row" className="permissions-matrix__module-cell">
                      {row.title}
                    </th>
                    {ACTIONS.map((a) => {
                      if (!applicableKeys.has(a.key)) {
                        return (
                          <td
                            key={a.key}
                            className="permissions-matrix__na"
                            aria-label={`${a.label} not applicable for ${row.title}`}
                          >
                            —
                          </td>
                        );
                      }
                      const checked = forceAllChecked
                        ? true
                        : row.permission_level.includes(a.key);
                      return (
                        <td key={a.key}>
                          <label className="permissions-matrix__checkbox">
                            <input
                              type="checkbox"
                              disabled={isReadOnly}
                              checked={checked}
                              onChange={() =>
                                updateRow(togglePermission(row, a.key))
                              }
                              aria-label={`${a.label} permission for ${row.title}`}
                            />
                            <span aria-hidden />
                          </label>
                        </td>
                      );
                    })}
                    <td>
                      <label className="permissions-matrix__checkbox">
                        <input
                          type="checkbox"
                          disabled={isReadOnly}
                          checked={rowAllChecked}
                          onChange={() => updateRow(toggleAll(row))}
                          aria-label={`All permissions for ${row.title}`}
                        />
                        <span aria-hidden />
                      </label>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PermissionsMatrix;
